import { makeAutoObservable, runInAction } from "mobx";
import { RootStore, ServiceStore } from "@intrasync/stores";
import {
    YardLocations,
    YardInventoryItem,
    YardMovementLogs,
    YardMaps,
    Trailer,
    LocationType,
    QCStatus,
} from "../types/YardTypes";

// Legacy aliases for backward compatibility
export interface YardLocation extends YardLocations { }
export interface YardMovementLog extends YardMovementLogs { }
export interface YardMap extends YardMaps { }

export class YardDashboardStore {
    rootStore: RootStore;

    // Loading states
    loading = false;
    error: string | null = null;

    // Selected items for operations
    selectedLocationId: number | null = null;
    selectedInventoryItemId: number | null = null;
    selectedTrailerId: number | null = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });
    }

    // Service store accessors
    get yardLocationsStore(): ServiceStore {
        return this.rootStore.yardLocationsStore as ServiceStore;
    }

    get yardInventoryItemsStore(): ServiceStore {
        return this.rootStore.yardInventoryItemsStore as ServiceStore;
    }

    get yardMovementLogsStore(): ServiceStore {
        return this.rootStore.yardMovementLogsStore as ServiceStore;
    }

    get yardMapsStore(): ServiceStore {
        return this.rootStore.yardMapsStore as ServiceStore;
    }

    get yardMapLocationAssignmentsStore(): ServiceStore {
        return this.rootStore.yardMapLocationAssignmentsStore as ServiceStore;
    }

    get trailerStore(): ServiceStore {
        return this.rootStore.trailerStore as ServiceStore;
    }

    // Computed values
    get yardLocations(): YardLocation[] {
        return this.yardLocationsStore.entities as unknown[] as YardLocation[];
    }

    get inventoryItems(): YardInventoryItem[] {
        return this.yardInventoryItemsStore.entities as unknown[] as YardInventoryItem[];
    }

    get movementLogs(): YardMovementLog[] {
        return this.yardMovementLogsStore.entities as unknown[] as YardMovementLog[];
    }

    get yardMaps(): YardMap[] {
        return this.yardMapsStore.entities as unknown[] as YardMap[];
    }

    get trailers(): Trailer[] {
        return this.trailerStore.entities as unknown[] as Trailer[];
    }

    get selectedLocation(): YardLocation | null {
        if (!this.selectedLocationId) return null;
        return this.yardLocations.find(loc => loc.id === this.selectedLocationId) || null;
    }

    get selectedInventoryItem(): YardInventoryItem | null {
        if (!this.selectedInventoryItemId) return null;
        return this.inventoryItems.find(item => item.id === this.selectedInventoryItemId) || null;
    }

    get selectedTrailer(): Trailer | null {
        if (!this.selectedTrailerId) return null;
        return this.trailers.find(trailer => trailer.id === this.selectedTrailerId) || null;
    }

    // Dashboard statistics
    get totalLocations(): number {
        return this.yardLocations.length;
    }

    get totalInventoryItems(): number {
        return this.inventoryItems.length;
    }

    get availableCapacity(): number {
        return this.yardLocations.reduce((total, location) => total + (location?.capacity || 0), 0);
    }

    get usedCapacity(): number {
        return this.inventoryItems.length; // Simplified - assuming 1 item per capacity unit
    }

    get utilizationPercentage(): number {
        if (this.availableCapacity === 0) return 0;
        return Math.round((this.usedCapacity / this.availableCapacity) * 100);
    }

    get availableTrailers(): Trailer[] {
        return this.trailers.filter(trailer => trailer.status === 'available');
    }

    get trailersInUse(): Trailer[] {
        return this.trailers.filter(trailer => trailer.status === 'in_use');
    }

    get recentMovements(): YardMovementLog[] {
        return this.movementLogs
            .slice()
            .sort((a, b) => new Date(b?.movedAt || Date.now()).getTime() - new Date(a?.movedAt || Date.now()).getTime())
            .slice(0, 10);
    }

    get inventoryByQcStatus() {
        const statusGroups = this.inventoryItems.reduce((acc: any, item: any) => {
            acc[item.qcStatus] = (acc[item.qcStatus] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        return {
            pending: statusGroups.pending || 0,
            passed: statusGroups.passed || 0,
            failed: statusGroups.failed || 0,
            rework: statusGroups.rework || 0,
        };
    }

    get locationsByType() {
        const typeGroups = this.yardLocations.reduce((acc, location) => {
            acc[location.locationType] = (acc[location.locationType] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        return typeGroups;
    }

    // Actions
    setSelectedLocation = (locationId: number | null) => {
        this.selectedLocationId = locationId;
    };

    setSelectedInventoryItem = (itemId: number | null) => {
        this.selectedInventoryItemId = itemId;
    };

    setSelectedTrailer = (trailerId: number | null) => {
        this.selectedTrailerId = trailerId;
    };

    setLoading = (loading: boolean) => {
        this.loading = loading;
    };

    setError = (error: string | null) => {
        this.error = error;
    };

    // Async actions
    refreshData = async () => {
        this.setLoading(true);
        this.setError(null);

        try {
            await Promise.all([
                this.yardLocationsStore.find({ query: { $limit: 100 } }),
                this.yardInventoryItemsStore.find({ query: { $limit: 200 } }),
                this.yardMovementLogsStore.find({ query: { $limit: 50 } }),
                this.yardMapsStore.find({ query: { $limit: 10 } }),
                this.trailerStore.find({ query: { $limit: 50 } }),
            ]);
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to refresh data');
            console.error('Failed to refresh yard data:', error);
        } finally {
            this.setLoading(false);
        }
    };

    moveInventoryItem = async (itemId: number, toLocationId: number, notes?: string) => {
        const item = this.inventoryItems.find(i => i.id === itemId);
        if (!item) {
            throw new Error('Inventory item not found');
        }

        try {
            // Create movement log
            await this.yardMovementLogsStore.create({
                inventoryItemId: itemId,
                fromLocationId: item.currentLocationId,
                toLocationId: toLocationId,
                movedByUserId: this.rootStore.authStore?.currentUser?.id || 1,
                quantityMoved: item.quantity,
                notes: notes,
                movedAt: new Date().toISOString(),
            });

            // Update inventory item location
            await this.yardInventoryItemsStore.patch(itemId, {
                currentLocationId: toLocationId,
            });

            // Refresh data
            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to move inventory item: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    updateTrailerStatus = async (trailerId: number, status: Trailer['status'], locationId?: number) => {
        try {
            await this.trailerStore.patch(trailerId, {
                status,
                ...(locationId && { currentLocationId: locationId }),
            });

            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to update trailer status: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    createYardLocation = async (locationData: Partial<YardLocation>) => {
        try {
            const newLocation = await this.yardLocationsStore.create({
                ...locationData,
                isActive: true,
            });

            await this.refreshData();
            return newLocation;
        } catch (error) {
            throw new Error(`Failed to create yard location: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    clear = () => {
        runInAction(() => {
            this.selectedLocationId = null;
            this.selectedInventoryItemId = null;
            this.selectedTrailerId = null;
            this.error = null;
            this.loading = false;
        });
    };
}

export default YardDashboardStore;
